<script lang="ts">
  import SalesActionDetailRow from './SalesActionDetailRow.svelte';
  import { anchorLawyerId } from './participant-lock';
  import {
    NEXT_ACTION_BY_STATUS,
    isNextActionAllowedForStatus,
  } from '@shared/constants/next-action-matrix';
  import { salesStatusToDisplay } from '@shared/constants/sales-action-display';
  import {
    ACTION_TYPE_LABELS,
    REVIEW_REQUIRED_REASON_LABELS,
    SALES_STATUS_LABELS,
    SALES_STATUS_ORDER,
    type ActionType,
    type SalesAction,
    type SalesStatus,
  } from '@shared/types/sales-action';
  import { VERIFICATION_STATUS_LABELS } from '@shared/types/customer-problem';
  import { withCurrentOption } from '@features/sales-action-edit/issue-tag-options';
  import type { Lawyer } from '@domain/lawyer/model';

  interface SolutionOption {
    id: string;
    label: string;
  }

  interface IssueTagOption {
    value: string;
    label: string;
  }

  interface Props {
    items: SalesAction[];
    canEdit: boolean;
    savingId: string | null;
    rowErrors: Record<string, string | null>;
    lawyers: Lawyer[];
    solutions: SolutionOption[];
    issueTagOptions: IssueTagOption[];
    /// 自分にとっての「更新」 (他ユーザーが last_seen 以降に追加・編集した) 現在アクションの
    /// id 集合 (Issue #524 PR3)。 行に「更新」バッジを出すかの判定に使う。
    unseenUpdateIds: Set<string>;
    openMenuId: string | null;
    setOpenMenuId: (id: string | null) => void;
    saveDueDate: (action: SalesAction, value: string) => void;
    saveActionType: (action: SalesAction, value: string) => void;
    saveActionTitle: (action: SalesAction, value: string) => void;
    saveIssueTag: (action: SalesAction, value: string) => void;
    saveNote: (action: SalesAction, value: string) => void;
    toggleParticipant: (action: SalesAction, lawyerId: string) => void;
    onStatusChange: (action: SalesAction, value: string) => void;
    saveSolution: (action: SalesAction, value: string) => void;
    /// 商談前の候補ソリューション (candidate_product_ids) を 1 件トグルする。
    toggleCandidate: (action: SalesAction, solutionId: string) => void;
    /// 商談以降 open 行から別ソリューションの商談行を追加する。
    addSolution: (action: SalesAction) => void;
    withdraw: (action: SalesAction) => void;
  }

  let {
    items,
    canEdit,
    savingId,
    rowErrors,
    lawyers,
    solutions,
    issueTagOptions,
    unseenUpdateIds,
    openMenuId,
    setOpenMenuId,
    saveDueDate,
    saveActionType,
    saveActionTitle,
    saveIssueTag,
    saveNote,
    toggleParticipant,
    onStatusChange,
    saveSolution,
    toggleCandidate,
    addSolution,
    withdraw,
  }: Props = $props();

  let expandedId = $state<string | null>(null);
  let participantPopoverId = $state<string | null>(null);
  let candidatePopoverId = $state<string | null>(null);

  // 商談前 = ソリューションを任意・複数候補として扱うステータス。
  // 商談以降 (negotiation_c/b/a) は単一・必須として扱う。
  const PRE_NEGOTIATION_STATUSES: SalesStatus[] = [
    'no_contact',
    'contacted',
    'in_dialogue',
    'appointment',
  ];
  function isPreNegotiation(status: SalesStatus): boolean {
    return PRE_NEGOTIATION_STATUSES.includes(status);
  }

  const solutionLabelById = $derived(
    Object.fromEntries(solutions.map((s) => [s.id, s.label])),
  );

  function candidateSummary(action: SalesAction): string {
    const ids = action.candidate_product_ids;
    const firstId = ids[0];
    if (firstId === undefined) return '未設定';
    const first = solutionLabelById[firstId] ?? firstId;
    return ids.length === 1 ? first : `${first} +${ids.length - 1}`;
  }

  const NEGOTIATION_STATUSES: SalesStatus[] = [
    'negotiation_c',
    'negotiation_b',
    'negotiation_a',
  ];
  // 「ソリューションを追加」 の表示条件: 商談 (negotiation_*) かつ open かつ solution 設定済み。
  function canAddSolution(action: SalesAction): boolean {
    return (
      NEGOTIATION_STATUSES.includes(action.sales_status) &&
      action.action_status === 'open' &&
      !!action.product_id
    );
  }

  const lawyerNameById = $derived(
    Object.fromEntries(lawyers.map((l) => [l.id, l.full_name])),
  );

  // 対象弁護士の必須対象 (86eya0dva 書き込み整合): 行自身のアンカー弁護士は解除不可。
  function handleToggleParticipant(action: SalesAction, lawyerId: string) {
    if (lawyerId === anchorLawyerId(action)) return;
    toggleParticipant(action, lawyerId);
  }

  function participantNames(action: SalesAction): string {
    return action.participant_lawyer_ids
      .map((id) => lawyerNameById[id] ?? id)
      .join('、 ');
  }

  function participantSummary(action: SalesAction): string {
    return participantNames(action) || '未設定';
  }

  // 課題の表示値 (AIスコア / 確認状態) は一覧応答同梱の source_problem を正とする
  // (86eya0dva PR3)。画面ターゲット単位の customer_problems 取得では、混在行
  // (別ターゲットの課題を参照する行) を解決できない。
  function aiScore(action: SalesAction): string {
    const score = action.source_problem?.ai_score;
    return score == null ? '—' : String(score);
  }

  function verificationLabel(action: SalesAction): string {
    const status = action.source_problem?.verification_status;
    if (!status) return '—';
    return VERIFICATION_STATUS_LABELS[status] ?? status;
  }

  // 候補外の現在値もセレクトに残す (暗黙解除しない)。
  function issueOptionsFor(action: SalesAction) {
    return withCurrentOption(
      issueTagOptions,
      action.source_customer_problem_id,
      action.source_problem?.problem_name ?? null,
    );
  }

  function actionTypeCandidates(status: SalesStatus): ActionType[] {
    return NEXT_ACTION_BY_STATUS[status];
  }

  function canSelectActionType(status: SalesStatus): boolean {
    return actionTypeCandidates(status).length > 0;
  }

  // 要確認の理由を「ルート」ラベル (自動 / 新規 / 更新) へ変換する (Issue #524 PR2)。
  // important_change は「要確認」バッジ自体が示すため、ここでは出さない (重複回避)。
  function reviewRouteLabel(action: SalesAction): string | null {
    const reason = action.review_required_reason;
    if (reason === null || reason === 'important_change') return null;
    return REVIEW_REQUIRED_REASON_LABELS[reason];
  }

  function handleStatusChange(action: SalesAction, value: string) {
    const next = value as SalesStatus;
    if (next === action.sales_status) return;
    if (
      next !== 'won' &&
      next !== 'lost' &&
      action.action_type &&
      !isNextActionAllowedForStatus(next, action.action_type)
    ) {
      saveActionType(action, '');
    }
    onStatusChange(action, value);
  }

  function toggleExpanded(action: SalesAction) {
    expandedId = expandedId === action.id ? null : action.id;
    setOpenMenuId(null);
    participantPopoverId = null;
    candidatePopoverId = null;
  }

  function toggleParticipantPopover(action: SalesAction) {
    participantPopoverId =
      participantPopoverId === action.id ? null : action.id;
    candidatePopoverId = null;
    setOpenMenuId(null);
  }

  function toggleCandidatePopover(action: SalesAction) {
    candidatePopoverId = candidatePopoverId === action.id ? null : action.id;
    participantPopoverId = null;
    setOpenMenuId(null);
  }

  function closePopoversOnOutsideClick(ev: MouseEvent) {
    const target = ev.target as HTMLElement | null;
    if (!target?.closest('.participant-popover')) participantPopoverId = null;
    if (!target?.closest('.candidate-popover')) candidatePopoverId = null;
  }
</script>

<svelte:window onclick={closePopoversOnOutsideClick} />

<!-- 現在の次アクション: 一覧上で高頻度項目をインライン編集 -->
<div class="table-scroll">
  <table>
    <colgroup>
      <col class="col-status" />
      <col class="col-next" />
      <col class="col-solution" />
      <col class="col-due" />
      <col class="col-participants" />
      <col class="col-actions" />
    </colgroup>
    <thead>
      <tr>
        <th class="col-status">ステータス</th>
        <th class="col-next">次アクション</th>
        <th class="col-solution">ソリューション</th>
        <th class="col-due">対応期限</th>
        <th class="col-participants">対象弁護士</th>
        <th class="col-actions" aria-label="操作"></th>
      </tr>
    </thead>
    <tbody>
      {#each items as action (action.id)}
        {@const rowDisabled = !canEdit || savingId === action.id}
        {@const isExpanded = expandedId === action.id}
        {@const detailId = `sales-action-detail-${action.id}`}
        {@const detailToggleId = `sales-action-detail-toggle-${action.id}`}
        {@const nextActionAllowed = isNextActionAllowedForStatus(
          action.sales_status,
          action.action_type,
        )}
        <tr
          data-testid="sales-action-row"
          data-action-status={action.action_status}
          data-display-status={salesStatusToDisplay(action.sales_status)}
        >
          <td class="col-status">
            <select
              class="cell-input"
              data-testid="status-select"
              value={action.sales_status}
              disabled={rowDisabled}
              onchange={(e) =>
                handleStatusChange(action, e.currentTarget.value)}
            >
              {#each SALES_STATUS_ORDER as s (s)}
                <option value={s}>{SALES_STATUS_LABELS[s]}</option>
              {/each}
            </select>
            {#if action.review_required}
              {@const routeLabel = reviewRouteLabel(action)}
              <!-- 要確認: 詳細を開いただけでは消えない。 自動/新規/更新 は作成経路、
                   要確認 は確認状態を示す (両方出てよい / Issue #524 PR2)。 -->
              <div class="review-badges" data-testid="review-required">
                {#if routeLabel}
                  <span class="badge badge-route">{routeLabel}</span>
                {/if}
                <span class="badge badge-review" aria-label="要確認あり"
                  >要確認</span
                >
              </div>
            {/if}
            {#if unseenUpdateIds.has(action.id)}
              <!-- 更新 (Issue #524 PR3): 他ユーザーが last_seen 以降に追加・編集した
                   現在アクション。 要確認 (確認状態) とは別概念で、 自分が営業アクション
                   タブを開くと次回から消える (ユーザー別の閲覧状態から導出)。 -->
              <div class="review-badges" data-testid="unseen-update">
                <span class="badge badge-update" aria-label="他ユーザーによる更新あり"
                  >更新</span
                >
              </div>
            {/if}
          </td>
          <td class="col-next">
            {#if canSelectActionType(action.sales_status)}
              <select
                class="cell-input"
                data-testid="action-type-select"
                aria-label="次アクション"
                value={nextActionAllowed ? (action.action_type ?? '') : ''}
                disabled={rowDisabled}
                onchange={(e) => saveActionType(action, e.currentTarget.value)}
              >
                <option value="">未設定</option>
                {#each actionTypeCandidates(action.sales_status) as t (t)}
                  <option value={t}>{ACTION_TYPE_LABELS[t]}</option>
                {/each}
              </select>
            {:else}
              <span class="muted">設定不可</span>
            {/if}
          </td>
          <td class="col-solution">
            {#if isPreNegotiation(action.sales_status)}
              <!-- 商談前: 候補ソリューション (任意・複数)。 popover で複数選択。 -->
              <div class="candidate-popover popover">
                <button
                  type="button"
                  class="cell-picker-trigger"
                  data-testid="candidate-trigger"
                  title={candidateSummary(action)}
                  aria-expanded={candidatePopoverId === action.id}
                  disabled={rowDisabled}
                  onclick={() => toggleCandidatePopover(action)}
                >
                  <span
                    class="picker-text"
                    class:muted={action.candidate_product_ids.length === 0}
                  >
                    {candidateSummary(action)}
                  </span>
                </button>
                {#if candidatePopoverId === action.id}
                  <div class="popover-menu candidate-menu">
                    {#if solutions.length === 0}
                      <p class="muted small">ソリューションがありません。</p>
                    {:else}
                      {#each solutions as s (s.id)}
                        <label class="check">
                          <input
                            type="checkbox"
                            checked={action.candidate_product_ids.includes(
                              s.id,
                            )}
                            disabled={rowDisabled}
                            onchange={() => toggleCandidate(action, s.id)}
                          />
                          <span>{s.label}</span>
                        </label>
                      {/each}
                    {/if}
                  </div>
                {/if}
              </div>
            {:else}
              <!-- 商談以降: 案件ソリューション (単一・必須)。 一覧からはクリア不可。 -->
              <select
                class="cell-input"
                class:needs-solution={!action.product_id}
                data-testid="solution-select"
                aria-label="ソリューション"
                value={action.product_id ?? ''}
                disabled={rowDisabled || solutions.length === 0}
                onchange={(e) => saveSolution(action, e.currentTarget.value)}
              >
                {#if !action.product_id}
                  <option value="" disabled>未設定（要選択）</option>
                {/if}
                {#each solutions as s (s.id)}
                  <option value={s.id}>{s.label}</option>
                {/each}
              </select>
            {/if}
          </td>
          <td class="col-due">
            <input
              type="date"
              class="cell-input"
              value={action.due_date ?? ''}
              disabled={rowDisabled}
              onchange={(e) => saveDueDate(action, e.currentTarget.value)}
            />
          </td>
          <td class="col-participants">
            <div class="participant-popover popover">
              <button
                type="button"
                class="lawyer-picker-trigger"
                title={participantSummary(action)}
                aria-expanded={participantPopoverId === action.id}
                disabled={rowDisabled}
                onclick={() => toggleParticipantPopover(action)}
              >
                <span
                  class:muted={action.participant_lawyer_ids.length === 0}
                  class="participant-text"
                >
                  {participantSummary(action)}
                </span>
              </button>
              {#if participantPopoverId === action.id}
                {@const rowAnchor = anchorLawyerId(action)}
                <div class="popover-menu participant-menu">
                  {#if lawyers.length === 0}
                    <p class="muted small">所属弁護士が登録されていません。</p>
                  {:else}
                    {#each lawyers as l (l.id)}
                      {@const isAnchor = l.id === rowAnchor}
                      <label class="check">
                        <input
                          type="checkbox"
                          checked={action.participant_lawyer_ids.includes(l.id)}
                          disabled={rowDisabled || isAnchor}
                          onchange={() => handleToggleParticipant(action, l.id)}
                        />
                        <span>{l.full_name}</span>
                        {#if isAnchor}
                          <em class="anchor-badge">必須</em>
                        {/if}
                      </label>
                    {/each}
                  {/if}
                </div>
              {/if}
            </div>
          </td>
          <td class="col-actions">
            <div class="row-actions">
              <button
                type="button"
                id={detailToggleId}
                class="detail-toggle"
                data-testid="sales-action-detail-toggle"
                aria-expanded={isExpanded}
                aria-controls={detailId}
                onclick={() => toggleExpanded(action)}
              >
                ▾ 詳細
              </button>
              <div class="popover">
                <button
                  type="button"
                  class="menu-trigger"
                  aria-label="操作メニュー"
                  disabled={!canEdit}
                  onclick={() => {
                    participantPopoverId = null;
                    setOpenMenuId(openMenuId === action.id ? null : action.id);
                  }}
                >
                  …
                </button>
                {#if openMenuId === action.id}
                  <div class="popover-menu align-right">
                    {#if canAddSolution(action)}
                      <button
                        type="button"
                        class="menu-item"
                        data-testid="add-solution-menu"
                        onclick={() => {
                          setOpenMenuId(null);
                          addSolution(action);
                        }}
                      >
                        ソリューションを追加
                      </button>
                    {/if}
                    <button
                      type="button"
                      class="menu-item danger"
                      onclick={() => withdraw(action)}
                    >
                      取下げ
                    </button>
                  </div>
                {/if}
              </div>
            </div>
          </td>
        </tr>
        {#if isExpanded}
          <SalesActionDetailRow
            colspan={6}
            id={detailId}
            labelledby={detailToggleId}
          >
            <div class="detail-grid">
              <label class="detail-field full">
                <span class="detail-label">次アクション詳細</span>
                <input
                  class="detail-input"
                  aria-label="次アクション詳細"
                  maxlength="200"
                  value={action.action_title ?? ''}
                  disabled={rowDisabled}
                  onblur={(e) => saveActionTitle(action, e.currentTarget.value)}
                  onkeydown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                />
              </label>

              <label class="detail-field">
                <span class="detail-label">課題</span>
                <select
                  class="detail-input"
                  aria-label="課題"
                  value={action.source_customer_problem_id ?? ''}
                  disabled={rowDisabled}
                  onchange={(e) => saveIssueTag(action, e.currentTarget.value)}
                >
                  <option value="">(なし)</option>
                  {#each issueOptionsFor(action) as option (option.value)}
                    <option value={option.value}>{option.label}</option>
                  {/each}
                </select>
              </label>

              <div class="detail-field readonly">
                <span class="detail-label">AI推認スコア</span>
                <span class="detail-value">{aiScore(action)}</span>
              </div>

              <div class="detail-field readonly">
                <span class="detail-label">確認状態</span>
                <span class="detail-value">{verificationLabel(action)}</span>
              </div>

              <label class="detail-field full">
                <span class="detail-label">メモ</span>
                <textarea
                  class="detail-input detail-textarea"
                  aria-label="メモ"
                  maxlength="1000"
                  rows="3"
                  value={action.note ?? ''}
                  disabled={rowDisabled}
                  onfocusout={(e) => saveNote(action, e.currentTarget.value)}
                ></textarea>
              </label>
            </div>
          </SalesActionDetailRow>
        {/if}
        {#if rowErrors[action.id]}
          <tr class="row-error">
            <td colspan="6">保存に失敗しました: {rowErrors[action.id]}</td>
          </tr>
        {/if}
      {/each}
    </tbody>
  </table>
</div>

<style>
  .table-scroll {
    align-self: stretch;
    width: 100%;
    overflow-x: auto;
    overflow-y: visible;
  }
  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  .col-status {
    width: 15%;
  }
  .col-next {
    width: 16%;
  }
  .col-solution {
    width: 19%;
  }
  .col-due {
    width: 13%;
  }
  .col-participants {
    width: 23%;
  }
  .col-actions {
    width: 14%;
  }
  th,
  td {
    padding: 8px;
    text-align: center;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: middle;
    min-width: 0;
  }
  th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f9fafb;
    color: #374151;
    font-weight: 600;
  }
  .cell-input,
  .detail-input {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding: 4px 6px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: white;
  }
  .cell-input {
    text-align: center;
  }
  .cell-input:disabled,
  .detail-input:disabled {
    background: #f3f4f6;
    opacity: 0.7;
  }
  /* 商談以降でソリューション未設定 (要選択) の警告。 */
  .needs-solution {
    border-color: #f59e0b;
    background: #fffbeb;
  }
  .participant-text,
  .picker-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    text-align: center;
  }
  .row-actions {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
  }
  .detail-toggle,
  .menu-trigger,
  .lawyer-picker-trigger,
  .cell-picker-trigger {
    box-sizing: border-box;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: white;
    cursor: pointer;
    font: inherit;
    white-space: nowrap;
  }
  .detail-toggle {
    padding: 3px 8px;
    font-size: 0.8125rem;
  }
  .lawyer-picker-trigger,
  .cell-picker-trigger {
    width: 100%;
    min-height: 29px;
    padding: 4px 6px;
    text-align: center;
  }
  .menu-trigger {
    padding: 2px 8px;
    line-height: 1;
  }
  .popover {
    position: relative;
  }
  .popover-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 20;
    min-width: 180px;
    max-height: 240px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
  .popover-menu.align-right {
    left: auto;
    right: 0;
    min-width: 120px;
  }
  .participant-menu {
    min-width: min(260px, 80vw);
  }
  .menu-item {
    text-align: left;
    padding: 6px 8px;
    border: none;
    background: none;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  .menu-item:hover {
    background: #f3f4f6;
  }
  .menu-item.danger {
    color: #b91c1c;
  }
  .detail-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }
  .detail-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    margin: 0;
  }
  .detail-field.full {
    grid-column: 1 / -1;
  }
  .detail-label {
    color: #4b5563;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .detail-value {
    min-height: 29px;
    display: flex;
    align-items: center;
  }
  .check {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    font-size: 0.8125rem;
    white-space: nowrap;
  }
  .anchor-badge {
    color: #b91c1c;
    font-style: normal;
    font-size: 0.6875rem;
  }
  .detail-textarea {
    resize: vertical;
    min-height: 72px;
  }
  .row-error td {
    padding: 4px 8px;
    color: #b91c1c;
    font-size: 0.8125rem;
    background: #fef2f2;
  }
  .muted {
    color: #6b7280;
  }
  .small {
    font-size: 0.75rem;
  }
  /* 要確認 / 自動・新規・更新 のラベル (Issue #524 PR2)。 件数バッジ (中立色) とは
     別意味なので、注意を引く配色にする。 */
  .review-badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
    margin-top: 4px;
  }
  .badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
  }
  .badge-review {
    color: #b45309;
    background: #fef3c7;
    border: 1px solid #fcd34d;
  }
  .badge-route {
    color: #1d4ed8;
    background: #dbeafe;
    border: 1px solid #bfdbfe;
  }
  /* 更新 (Issue #524 PR3)。 要確認 (amber) とは別概念なので別配色 (緑系) にする。 */
  .badge-update {
    color: #047857;
    background: #d1fae5;
    border: 1px solid #6ee7b7;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 860px) {
    .detail-grid {
      grid-template-columns: 1fr;
    }
    .detail-field.full {
      grid-column: auto;
    }
    .row-actions {
      flex-direction: column;
      align-items: center;
    }
  }
</style>
